import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import SEO, { orgJsonLd, breadcrumbJsonLd } from "../components/SEO";
import Reveal from "../components/Reveal";
import Icon from "../components/Icon";
import { CASE_STUDIES } from "../data/siteData";

export default function CaseStudiesPage() {
  return (
    <>
      <SEO
        title="Case Studies — ₹58 Cr Series A, 3x Growth & More | Complisure Client Success"
        description="Real outcomes: fundraising closes, Virtual CFO turnarounds, restructuring and compliance recoveries across Hyderabad, Bengaluru, Chennai, Gurugram and more."
        canonical="https://compliance-capital-1.preview.emergentagent.com/case-studies"
        jsonLd={[orgJsonLd, breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Case Studies", path: "/case-studies" }])]}
      />
      <section className="relative overflow-hidden pb-20 pt-36">
        <div className="blob blob-float right-[-8%] top-[-8%] h-96 w-96 bg-brand-blue/10" />
        <div className="dot-grid absolute inset-0 opacity-[0.1]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <h1 className="max-w-3xl font-display text-4xl font-extrabold tracking-[-0.02em] text-brand-navy sm:text-5xl">
              Outcomes, not <span className="text-gradient">activity reports</span>
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-brand-gray sm:text-lg">
              Client names anonymised where required — the numbers are real, and verifiable in a reference call once we engage.
            </p>
          </Reveal>

          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {CASE_STUDIES.map((cs, i) => (
              <Reveal key={cs.client} delay={(i % 2) * 0.1}>
                <article data-testid={`case-study-${cs.client.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="glass card-lift flex h-full flex-col rounded-3xl p-8">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="rounded-full bg-brand-ice px-3 py-1 text-xs font-semibold text-brand-blue">{cs.industry}</span>
                      <span className="flex items-center gap-1 text-xs text-brand-gray"><MapPin className="h-3 w-3" /> {cs.city}</span>
                    </div>
                    <span className="font-display text-sm font-extrabold text-brand-navy">{cs.amount}</span>
                  </div>
                  <h2 className="mt-4 font-display text-xl font-bold tracking-tight text-brand-navy">{cs.title}</h2>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-brand-gray">{cs.summary}</p>
                  <ul className="mt-5 space-y-2 border-t border-brand-blue/10 pt-4">
                    {cs.results.map((r) => (
                      <li key={r} className="flex items-start gap-2.5 text-sm text-brand-navy/85">
                        <Icon name="CheckCircle2" className="mt-0.5 h-4 w-4 shrink-0 text-brand-blue" /> {r}
                      </li>
                    ))}
                  </ul>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-16 text-center">
            <div className="glass mx-auto max-w-2xl rounded-3xl p-10">
              <h2 className="font-display text-2xl font-bold tracking-tight text-brand-navy">Want to be the next case study?</h2>
              <p className="mt-3 text-sm text-brand-gray sm:text-base">Tell us the outcome you're chasing — funding, clean compliance, a restructure — and we'll map the route.</p>
              <Link to="/contact" data-testid="cases-cta" className="btn-gradient mt-6 inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-white">
                Start the Conversation <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
